import { CSSProperties, ReactElement } from 'react';
import { DatePicker, ConfigProvider, DatePickerProps } from 'antd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import { theme } from '../../../theme';

const { RangePicker } = DatePicker;

// Type for the input value (accepts Date, string, Dayjs, or null)
type InputValue = Date | string | null;
type InputRangeValue = [InputValue, InputValue] | null;

interface Properties {
  dataTestId?: string;
  disabled?: boolean;
  // Use any type to accept all kinds of date inputs
  value?: any;
  placeholder?: string;
  endPlaceholder?: string;
  dateFormat?: string;
  showIcon?: boolean;
  // Use any type for onChange handler
  onChange?: (dates: any) => void;
  sx?: CSSProperties;
  allowClear?: boolean;
  disabledDate?: DatePickerProps['disabledDate'];
  popupStyle?: CSSProperties;
}

export function DateRangePickerField({
  dataTestId,
  placeholder = '',
  endPlaceholder = '',
  onChange,
  value,
  disabled,
  sx = {},
  dateFormat,
  showIcon = false,
  allowClear = false,
  disabledDate,
  popupStyle,
}: Properties): ReactElement {
  // Convert input values to Dayjs for AntD's RangePicker
  const pickerValue = value
    ? [value[0] ? dayjs(value[0]) : null, value[1] ? dayjs(value[1]) : null]
    : null;

  // Handle changes from AntD's RangePicker
  const handleChange = (dates: any, dateStrings: [string, string]) => {
    if (!onChange) return;

    // Convert back to the same format as the input for consistency
    const resultDates = dates
      ? [dates[0] ? dates[0].toDate() : null, dates[1] ? dates[1].toDate() : null]
      : null;

    onChange(resultDates);
  };

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: theme.palette.primary.main },
      }}
    >
      <RangePicker
        placeholder={[placeholder, endPlaceholder]}
        format={dateFormat ?? 'MMM D, YY'}
        disabled={disabled}
        value={pickerValue as any}
        onChange={handleChange}
        allowClear={allowClear}
        suffixIcon={showIcon ? <ExpandMoreIcon /> : undefined}
        style={sx}
        data-testid={dataTestId}
        disabledDate={disabledDate}
        popupStyle={popupStyle}
      />
    </ConfigProvider>
  );
}
